import { Space, Button } from 'antd';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { type WebCalcSettingDataFragment } from '../../../../api/fragments/WebCalcSettingData';
import DrawerWithAutoWidth from '../../../../components/DrawerWithAutoWidth';
import RemoteFPSettingForm from './RemoteFPSettingForm';

type State = {
    setting?: WebCalcSettingDataFragment;
    visible: boolean;
};

type OpenAction = {
    type: 'open';
    setting?: WebCalcSettingDataFragment;
};

type CloseAction = {
    type: 'close';
};

type Action = OpenAction | CloseAction;

export type RemoteFPSettingsDrawerProps = {
    onClose: () => void;
    onSubmitted: OnRemoteFPSettingSubmitted;
    state: State;
    moduleId: string;
};

const RemoteFPSettingsDrawer = ({ onClose, onSubmitted, state, moduleId }: RemoteFPSettingsDrawerProps) => {
    const { t } = useTranslation(['bankModuleDetails']);
    const isEdit = !!state.setting;

    const onComplete = useCallback(() => {
        onSubmitted();

        onClose();
    }, [onClose, onSubmitted]);

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('bankModuleDetails:remoteFpSetting.drawer.buttons.cancel')}</Button>
                    <Button form="remoteFPSettingForm" htmlType="submit" type="primary">
                        {t('bankModuleDetails:remoteFpSetting.drawer.buttons.submit')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t(`bankModuleDetails:remoteFpSetting.drawer.titles.${isEdit ? 'update' : 'create'}`)}
            visible={state.visible}
            destroyOnClose
        >
            <RemoteFPSettingForm moduleId={moduleId} onComplete={onComplete} setting={state.setting} />
        </DrawerWithAutoWidth>
    );
};

export default RemoteFPSettingsDrawer;

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'open':
            return { visible: true, setting: action.setting };

        case 'close':
            return { visible: false };
    }

    return state;
};

export type OpenRemoteFPSettingDrawer = (setting?: WebCalcSettingDataFragment) => void;
export type OnRemoteFPSettingSubmitted = () => void;
export type SetOnRemoteFPSettingSubmitted = Dispatch<SetStateAction<OnRemoteFPSettingSubmitted>>;
export const useRemoteFPSettingDrawer = (moduleId: string, onSubmitted: OnRemoteFPSettingSubmitted) => {
    const [state, dispatch] = useReducer(reducer, { visible: false });

    const actions = useMemo(
        () => ({
            open: (setting?: WebCalcSettingDataFragment) => {
                dispatch({ type: 'open', setting });
            },
            close: () => {
                dispatch({ type: 'close' });
            },
        }),
        []
    );

    return {
        ...actions,
        render: () => (
            <RemoteFPSettingsDrawer
                moduleId={moduleId}
                onClose={actions.close}
                onSubmitted={onSubmitted}
                state={state}
            />
        ),
    };
};
